import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];

const RevealLine = ({ children, delay = 0, className = "" }) => (
  <span className="reveal-mask">
    <motion.span
      className={`inline-block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden pt-28 md:pt-32 flex flex-col"
      data-testid="hero-section"
    >
      {/* Top labels */}
      <div className="grid grid-cols-12 px-6 md:px-10 lg:px-14 border-b hairline pb-4">
        <div className="col-span-6 md:col-span-3 label">
          <RevealLine delay={0.2}>Founder — Hypmmedia</RevealLine>
        </div>
        <div className="hidden md:block col-span-3 label">
          <RevealLine delay={0.3}>New Delhi · IN</RevealLine>
        </div>
        <div className="hidden md:block col-span-3 label">
          <RevealLine delay={0.4}>Creative · Strategy · Client Success</RevealLine>
        </div>
        <div className="col-span-6 md:col-span-3 label text-right">
          <RevealLine delay={0.5}>Est. Hypmmedia</RevealLine>
        </div>
      </div>

      {/* Massive name */}
      <motion.div
        style={{ y: textY }}
        className="relative px-6 md:px-10 lg:px-14 pt-8 md:pt-10"
      >
        <div className="font-serif leading-[0.86] tracking-tight text-ink">
          <div className="reveal-mask">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1.3, ease: EASE, delay: 0.6 }}
              className="text-[14vw] md:text-[11vw] block font-light"
            >
              Akanksha
            </motion.h1>
          </div>
          <div className="flex items-end justify-between mt-1 md:mt-2">
            <div className="reveal-mask">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.3, ease: EASE, delay: 0.85 }}
                className="text-[14vw] md:text-[11vw] italic block font-light"
              >
                Chandok.
              </motion.span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Bottom row — sits below the name with generous spacing */}
      <div className="px-6 md:px-10 lg:px-14 pt-20 md:pt-32 pb-8 mt-auto flex items-end justify-between gap-6">
        <div>
          <RevealLine delay={1.2}>
            <span className="label">Scroll</span>
          </RevealLine>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-2"
          >
            <ArrowDown className="w-4 h-4 text-mute" strokeWidth={1} />
          </motion.div>
        </div>
        <div className="max-w-sm hidden md:block text-right">
          <RevealLine delay={1.15}>
            <span className="font-serif italic text-lg md:text-xl text-ink/90">
              A modern digital studio built on
            </span>
          </RevealLine>
          <RevealLine delay={1.25}>
            <span className="font-serif italic text-lg md:text-xl text-ink/90">
              strategy, storytelling &amp; smart execution.
            </span>
          </RevealLine>
        </div>
      </div>
    </section>
  );
};

export default Hero;
